import { WellDetailsStates } from 'business/models/CustomModels';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DropDetails, ImageSizeDetails, WellDisplayMode } from './constants';
import { EGridLayout } from 'components/grid/constants';
import { getGridStyles } from 'components/grid/utils';
import WellContainer, { Props as WellContainerProps } from './WellContainer';
import { useRMDispatch } from 'redux/hooks';
import { PlateWellDTO } from 'business/models/Models';
import { isEqual } from 'lodash';
import {
  useIsAnyWellDropImageAvailable,
  useWellsIndependentZoom,
} from 'redux/hooks/well';
import {
  selectWellImagingSettingDetails,
  setIsDropImageAvailable,
} from 'redux/reducers/experimentDataReducer';
import { DropImageParameters, DropUrlParameters } from 'components/Experiment/constants';
import { history } from 'utils/Global';

export interface Props {
  wells: WellContainerProps[] | any[];
  displayMode: WellDisplayMode;
  gridLayout: EGridLayout;
  setIsWellLoaded?: React.Dispatch<React.SetStateAction<boolean>>;
  setZoomInvocker?: (onZoom: (zoom: number) => void) => void;
  selectedWellDetails?: WellDetailsStates | DropDetails;
  imagedWells?: PlateWellDTO[];
  isTraversingVertical?: boolean;
  WellRenderer?: any;
  selectedWellIndex?: number;
  isFocus?: boolean;
  setWellRef?: (wellKey: string, ref: any) => void;
  wellKeyGenerator: (wellDetails: WellDetailsStates | DropDetails) => string;
}
const FIXED_COMPARE = false;
let wellsImagesSizeDetails: ImageSizeDetails[] = [];

function WellProcessor(props: Props) {
  const {
    wells,
    displayMode,
    gridLayout,
    setIsWellLoaded,
    setZoomInvocker,
    selectedWellDetails,
    imagedWells,
    isTraversingVertical = false,
    WellRenderer = WellContainer,
    selectedWellIndex = undefined,
    isFocus,
    setWellRef,
    wellKeyGenerator,
  } = props;

  const dispatch = useRMDispatch();
  const [maxImageSizeDetails, setMaxImageSizeDetails] = useState(null);
  const [minImageSizeDetails, setMinImageSizeDetails] = useState(null);
  const [stageDetails] = useState(null);
  const isAnyImageAvailable = useIsAnyWellDropImageAvailable(
    wells.map((well) => well.wellDetails as WellDetailsStates),
  );
  const wellsIndependentZoom = useWellsIndependentZoom();
  const stageRefs = useRef<{ [key: string]: any }>({});
  const zoomLevels = useRef<{ [key: string]: any }>({});
  const [independentZoomLevels, setIndependentZoomLevels] = useState<{
    [key: string]: any;
  }>({});

  useEffect(() => {
    dispatch(setIsDropImageAvailable(isAnyImageAvailable));
  }, [isAnyImageAvailable]);

  const updateWellsImagesSizeDetails = (sizeDetails: ImageSizeDetails) => {
    const filteredDetails = wellsImagesSizeDetails.filter(
      (imgDetail) => !isEqual(imgDetail.wellDetails, sizeDetails.wellDetails),
    );

    const updatedDetails = [...filteredDetails, sizeDetails];
    wellsImagesSizeDetails = updatedDetails;

    if (!FIXED_COMPARE) {
      setImageDetails(updatedDetails);
    }
  };

  const setImageDetails = (detailsParam?: ImageSizeDetails[]) => {
    const details = detailsParam ?? wellsImagesSizeDetails;

    if (details.length === 0) {
      return;
    }

    const sorted = [...details].sort((x, y) => {
      const n = x.width - y.width;
      return n !== 0 ? n : x.height - y.height;
    });

    const max = sorted[sorted.length - 1];
    const min = sorted[0];

    if (maxImageSizeDetails === max && minImageSizeDetails === min) {
      return;
    }

    setMaxImageSizeDetails(max);
    setMinImageSizeDetails(min);
  };

  const setInitialZoom = () => {
    if (!minImageSizeDetails) {
      return;
    }

    let scaleToMatchMinImage = 1;

    Object.values(stageRefs.current).forEach((ref) => {
      const containerWidth = ref.attrs.width;
      const containerHeight = ref.attrs.height;

      const minImageWidth = minImageSizeDetails.width;
      const minImageHeight = minImageSizeDetails.height;
      const maxImageWidth = maxImageSizeDetails.width;
      const maxImageHeight = maxImageSizeDetails.height;

      const baseScale = Math.min(
        containerWidth / minImageWidth,
        containerHeight / minImageHeight,
      );

      const maxImageFitScale = Math.min(
        containerWidth / maxImageWidth,
        containerHeight / maxImageHeight,
      );

      scaleToMatchMinImage = baseScale / maxImageFitScale;

      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;

      const newPos = {
        x: centerX - centerX * scaleToMatchMinImage,
        y: centerY - centerY * scaleToMatchMinImage,
      };

      ref.scale({ x: scaleToMatchMinImage, y: scaleToMatchMinImage });
      ref.position(newPos);
      ref.batchDraw();
    });

    wells.forEach((well) => {
      const wellKey = wellKeyGenerator(well.wellDetails);
      zoomLevels.current[wellKey] = scaleToMatchMinImage;
    });
  };

  const clearStage = () => {
    Object.values(stageRefs.current).forEach((ref) => {
      ref.scale({ x: 1, y: 1 });
      ref.position({ x: 0, y: 0 });
      ref.batchDraw();
    });
  };

  useEffect(() => {
    if (wells?.length > 1 && minImageSizeDetails) {
      setInitialZoom();
    }

    return () => {
      clearStage();
    };
  }, [
    wells?.length,
    minImageSizeDetails?.width,
    minImageSizeDetails?.height,
    minImageSizeDetails?.scalingWidth,
    minImageSizeDetails?.scalingHeight,
  ]);

  /**
   * Handles the zoom level changes for wells and updates the corresponding stage references.
   *
   * @param stage - The stage object of the currnet selected well.
   * @param newScale - The new zoom scale to be applied to the currnet selected well.
   * @param bestFit - A boolean indicating whether the zoom should fit the content to the container.
   * @param wellKey - The unique identifier for the well being zoomed.
   *
   * This function performs the following:
   * - Calculates the change in zoom level for the specified well and zooms other wells accordingly.
   * - Updates and maintains independent zoom levels for each well.
   * - Ensures that synchronized zooming respects the independent zoom levels of wells.
   * - Adjusts the zoom and position of other wells based on the zoom context.
   * - Prevents zooming beyond the boundaries of the stage.
   * - Prevents wells from zooming out more than their independent zoom level.
   */
  const onZoomChange = (stage, oldScale, newScale, bestFit, wellKey) => {
    const prevScale = zoomLevels.current[wellKey] || oldScale;
    const delta = newScale - prevScale;
    zoomLevels.current[wellKey] = newScale;
    // ───────────────────  Per-well zoom handling  ───────────────────
    //
    // • Always keep a per-well “independent zoom” value.
    //
    // • If multiple wells are shown AND this well is in independent-zoom mode
    //   → remember just this well’s zoom level.
    // • If “Best Fit” is triggered → clear all per-well zoom overrides
    //   (sets every stored zoom back to 1).
    // ─────────────────────────────────────────────────────────────────
    if (wells.length > 1 && wellsIndependentZoom[wellKey] === true) {
      setIndependentZoomLevels((prev) => ({
        ...prev,
        [wellKey]: newScale,
      }));
    }
    if (bestFit) {
      setIndependentZoomLevels((prev) =>
        Object.fromEntries([...Object.keys(prev)].map((k) => [k, 1])),
      );
    }
    if (
      (wellsIndependentZoom[wellKey] && !bestFit) ||
      displayMode === WellDisplayMode.Drop
    ) {
      return;
    }

    const filteredStageRefs = (
      bestFit
        ? Object.keys(stageRefs.current)
        : Object.keys(stageRefs.current).filter(
            (key) =>
              !(key in wellsIndependentZoom && wellsIndependentZoom[key]) &&
              key !== wellKey,
          )
    ).map((key) => {
      const ref = stageRefs.current[key];
      ref.wellKey = key;
      return ref;
    });

    filteredStageRefs.forEach((ref) => {
      if (bestFit) {
        ref.scale({ x: newScale, y: newScale });
        ref.position({ x: stage.x(), y: stage.y() });
        ref.batchDraw();
        zoomLevels.current[ref.wellKey] = newScale;
      } else {
        const oldScaleRef = ref.scaleX();
        let updatedScale = oldScaleRef + delta;
        if (updatedScale < independentZoomLevels?.[ref.wellKey]) {
          updatedScale = independentZoomLevels?.[ref.wellKey];
        }

        if (updatedScale >= 1) {
          zoomLevels.current[ref.wellKey] = updatedScale;

          const stageWidth = ref.width();
          const stageHeight = ref.height();
          const center = {
            x: stageWidth / 2,
            y: stageHeight / 2,
          };

          const zoomRefPointTo = {
            x: (center.x - stage.x()) / stage.scaleX(),
            y: (center.y - stage.y()) / stage.scaleX(),
          };

          let newPos = {
            x: center.x - zoomRefPointTo.x * updatedScale,
            y: center.y - zoomRefPointTo.y * updatedScale,
          };

          if (updatedScale <= 1) {
            newPos = { x: 0, y: 0 };
          }
          if (newPos.x > 0) {
            newPos.x = 0;
          }
          if (newPos.y > 0) {
            newPos.y = 0;
          }

          ref.scale({ x: updatedScale, y: updatedScale });
          ref.position(newPos);
          ref.batchDraw();
        }
      }
    });
  };

  const setStageRef = (ref, identifier, wellKey) => {
    if (ref) {
      stageRefs.current[wellKey] = ref;
    } else {
      delete stageRefs.current[wellKey];
    }
  };

  const updateSelectedDetails = (details: WellDetailsStates) => {
    const { plateID, wellNum, cpID, dropNumber, regionName, inspectionSet } = details;
    const imageBatchId = inspectionSet?.groupedInspections.find(
      (insp) => insp.plateId === plateID,
    )?.imageBatchId;
    if (!imageBatchId || !plateID || !wellNum || !dropNumber || !cpID || !regionName) {
      return;
    }

    const parts = window.location.pathname.split('/');
    parts[DropUrlParameters.PLATEID] = plateID.toString();
    parts[DropUrlParameters.WELLNUM] = wellNum.toString();
    parts[DropUrlParameters.DROPNUMBER] = dropNumber.toString();
    parts[DropUrlParameters.CPID] = cpID.toString();
    parts[DropUrlParameters.IMAGEBATCHID] = imageBatchId.toString();
    history.replace(parts.join('/'));
    const DropImageParameters: DropImageParameters = {
      cpId: cpID,
      dropNumber,
      regionName,
      inspection: inspectionSet,
    };
    dispatch(selectWellImagingSettingDetails(DropImageParameters));
  };

  const isWellSelected = (well, wellIndex) => {
    if (selectedWellIndex !== undefined) {
      return wellIndex === selectedWellIndex;
    }
    return isEqual(well, selectedWellDetails);
  };

  const gridStyle = getGridStyles(wells?.length, gridLayout as EGridLayout);

  const wellComponents = useMemo(() => {
    wellsImagesSizeDetails = wellsImagesSizeDetails.filter((w) =>
      wells.find((n) => isEqual(n.wellDetails, w.wellDetails)),
    );
    setImageDetails();

    return wells.map((wellProps, index) => {
      const wellKey = wellKeyGenerator(wellProps.wellDetails);
      const commonProps = {
        wellKey,
        isIndependentZoom: wellsIndependentZoom[wellKey],
        setIsWellLoaded,
        setZoomInvocker,
        isMultipleDropsRoute:
          displayMode === WellDisplayMode.Compare || displayMode === WellDisplayMode.Well,
        isTraversingVertical,
        imagedWells,
        wellIndex: index,
        displayMode: displayMode,
        selectedWellsLength: wells?.length,
        isWellInFocus: isFocus,
        setWellRef,
        onZoomChange,
      };

      const conditionalProps =
        displayMode === WellDisplayMode.Compare || displayMode === WellDisplayMode.Well
          ? {
              updateWellsImagesSizeDetails,
              maxImageSizeDetails,
              stageDetails,
              setStageRef: setStageRef,
              updateSelectedDetails,
              selectedWell: selectedWellDetails,
              minZoomLevel: independentZoomLevels?.[wellKey],
              totalWells: wells?.length,
            }
          : {};

      return (
        <div
          id={`wellprocessor-component-${index}`}
          key={index}
          style={{
            margin: 5,
            outline: isWellSelected(wellProps.wellDetails, index)
              ? `5px solid ${isFocus === false ? 'grey' : 'orange'}`
              : null,
          }}
        >
          <WellRenderer
            id={`wellprocessor-well-${index}`}
            key={`well-${index}`} // Don't forget to add a unique key for each component
            {...wellProps}
            {...commonProps}
            {...conditionalProps}
          />
        </div>
      );
    });
  }, [
    displayMode,
    wells,
    updateWellsImagesSizeDetails,
    maxImageSizeDetails,
    stageDetails,
    selectedWellDetails,
    independentZoomLevels,
  ]);

  if (!wells?.length) {
    return <div id="wellprocessor-waiting-message">Loading...</div>;
  }
  return (
    <div id="wellprocessor-container" style={gridStyle}>
      {wellComponents}
    </div>
  );
}

export default WellProcessor;
